import { LocalStorageService } from '@mpt-fo/common/core/services/local-storage.service';
import { TimelineModel } from '@mpt-fo/common/shared/models/timeline.model';
import { StringHelper } from '@mpt-fo/common/core/helpers/strings.helper';
import { AuthenticationService } from '@mpt-fo/common/core/services/authentication.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Component, OnInit } from '@angular/core';
import { ScreenManagerService } from '@mpt-fo/common/core/services/screen-manager.service';
import { Router } from '@angular/router';
import { ErrorCode } from '@app/channels/onboarding/onboarding-v2/model/onboarding-v2-model';
import { IdentityProvider } from '@mpt-fo/common/kamona/model/kamonal-model';

@Component({
	selector: 'app-forgot-password',
	templateUrl: './forgot-password.component.html',
	styleUrls: ['./forgot-password.component.scss'],
})
export class ForgotPasswordComponent implements OnInit {
	validateForm!: FormGroup;
	submitting: boolean;
	showSuccess: boolean;
	errorMessage: string;
	stepsArray: TimelineModel[];
	currentStep: TimelineModel;
	currentStepIndex: number;
	finalStepIndex: number;
	forgotPasswordObj: any;
	showFaceCapture: boolean;
	doFaceCheck: any;
	isMobile: boolean;
	ROUTE = '/password-reset';
	useOtp = false;
	livelinessCheckReference = '';
	identityProvider = IdentityProvider.METAMAP;
	constructor(
		private fb: FormBuilder,
		private auth: AuthenticationService,
		private localStorage: LocalStorageService,
		private screenManager: ScreenManagerService,
		private router: Router
	) {}

	ngOnInit(): void {
		this.localStorage.deleteAll();
		if (this.screenManager.isMobile()) {
			this.mobileRoute();
		}
		this.screenManager.isMobile$.subscribe((isMobile) => {
			if (isMobile && this.router?.url === this.ROUTE) {
				this.mobileRoute();
			}
		});
		this.validateForm = this.fb.group({
			username: [null, [Validators.required]],
		});
		this.createSteps();
		this.initializeStep();
	}

	mobileRoute() {
		this.router.navigateByUrl('/mobile/recovery/password');
	}

	submitForm() {
		for (const i in this.validateForm.controls) {
			if (this.validateForm && this.validateForm.controls) {
				this.validateForm.controls[i].markAsDirty();
				this.validateForm.controls[i].updateValueAndValidity();
			}
		}

		if (this.validateForm.invalid) {
			return;
		}
		this.localStorage.deleteAll();
		this.forgotPassword();
	}

	forgotPassword() {
		this.errorMessage = null;
		this.submitting = true;
		const params = this.validateForm.value;
		this.auth
			.forgotPassword(params)
			.then((res) => {
				this.submitting = false;
				this.showSuccess = true;
			})
			.catch((err) => {
				this.errorMessage = StringHelper.getErrorMessageHttpErrorResponse(err);
				this.submitting = false;
			});
	}

	proceed(val?) {
		this.currentStep.data = val;
		this.setOtpMessage();
		this.checkFaceValidationFlag();
		if (this.currentStepIndex === this.finalStepIndex) {
			this.stepsArray.map((x) => {
				this.forgotPasswordObj = { ...this.forgotPasswordObj, ...x.data };
			});
			return;
		}
		this.updateStep();
	}

	setOtpMessage() {
		if (this.currentStep.number === 1) {
			this.useOtp = this.currentStep?.response?.code !== ErrorCode.LIVELINESS_REQUIRED;
			if (this.useOtp) {
				this.stepsArray[1].description = this.currentStep?.response?.otpMessage;
			} else {
				this.stepsArray[1].title = 'Face Verification';
				this.stepsArray[1].description = '';
				this.livelinessCheckReference = this.currentStep?.response?.reference || '';
				if (!this.useOtp) {
					this.identityProvider = this.currentStep?.response?.provider || IdentityProvider.METAMAP;
				}
			}
		}
	}

	checkFaceValidationFlag() {
		if (this.currentStep.number !== 2) {
			return;
		}
		this.doFaceCheck = this.currentStep.response.liveliness;
	}

	updateStep() {
		this.currentStep.state = 'completed';
		if (this.currentStep.number === 2 && !this.doFaceCheck) {
			this.currentStepIndex += 2;
		} else {
			this.currentStepIndex++;
		}
		this.currentStep = this.stepsArray[this.currentStepIndex];
		this.currentStep.state = 'active';
	}

	goBack(num = this.currentStep.number) {
		const index = num - 1;
		if (this.currentStepIndex === 0) {
			return;
		}
		if (this.currentStep.state === 'active') {
			this.currentStep.state = 'inactive';
		}
		this.currentStepIndex = index - 1;
		this.currentStep = this.stepsArray[this.currentStepIndex];
		this.currentStep.state = 'active';
	}

	getCurrentStepProgress = () => {
		if (!this.stepsArray || this.stepsArray.length < 1) {
			return 0;
		}
		this.currentStep = this.stepsArray.find((x) => x.state === 'active');
		const percent = (100 / this.stepsArray.length) * this.currentStep.number;
		return percent;
	}

	private createSteps() {
		this.stepsArray = [
			{
				state: 'active',
				number: 1,
				title: 'Password Recovery',
				description: 'Let\'s get you sorted out',
			},
			{
				state: 'inactive',
				number: 2,
				title: 'Verify your phone number',
				description: `We’ve just sent a 6 digit code to your phone
				Check your messages and enter it here`,
			},
			{
				state: 'inactive',
				number: 3,
				title: 'Face Capture!',
				description: 'Enter your Face',
			},
			{
				state: 'inactive',
				number: 4,
				title: 'Yay! You are all done! ',
				description: 'Your Moniepoint account has been created successfully',
			},
		];
	}

	private initializeStep() {
		this.currentStepIndex = 0;
		this.finalStepIndex = this.stepsArray.length - 1;
		this.currentStep = this.stepsArray[this.currentStepIndex];
	}
}
